/**
 * Cloudflare Turnstile（Managed）人机验证。
 * Site Key 为公开标识，可随前端发布。
 * 仅用于登录 / 注册 / 发短信 / 找回密码 / 社交登录，不要加到普通业务请求。
 */
export const DEVICE_ID_STORAGE_KEY = "lychee.turnstile.device-id";
export const TURNSTILE_SLOT_ATTR = "data-turnstile-slot";
export const TURNSTILE_SITE_KEY = "0x4AAAAAAFSw9f_FWWiPE3bR";
export const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
export const VERIFY_TIMEOUT_MS = 120_000;
export const VERIFY_ERROR_MESSAGE = "人机验证失败，请刷新后重试";
export const TURNSTILE_VERIFY_FAILED_CODE = 1002000011;
export const TURNSTILE_UNAVAILABLE_CODE = 1002000012;

export type TurnstileAction =
  | "login"
  | "sms_login"
  | "sms_send"
  | "register"
  | "password_reset"
  | "social_login";

interface TurnstileWidgetOptions {
  sitekey: string;
  action: TurnstileAction;
  cData: string;
  execution: "execute";
  appearance: "always";
  callback: (token: string) => void;
  "error-callback": () => void;
  "expired-callback": () => void;
  "timeout-callback": () => void;
}

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileWidgetOptions) => string;
  execute: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstileScriptPromise: Promise<TurnstileApi> | undefined;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScriptPromise) return turnstileScriptPromise;

  const script = document.createElement("script");
  script.src = TURNSTILE_SCRIPT_URL;
  script.async = true;
  script.defer = true;

  const loading = new Promise<TurnstileApi>((resolve, reject) => {
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error(VERIFY_ERROR_MESSAGE));
    };
    script.onerror = () => {
      script.remove();
      reject(new Error(VERIFY_ERROR_MESSAGE));
    };
    document.head.appendChild(script);
  });

  turnstileScriptPromise = loading.catch((error) => {
    turnstileScriptPromise = undefined;
    throw error;
  });
  return turnstileScriptPromise;
}

/** 首次访问生成并持久化设备 ID；登录、注册、刷新、退出后都复用。 */
export function getDeviceId(): string {
  const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (existing && /^[A-Za-z0-9_-]{16,128}$/.test(existing)) return existing;

  if (typeof crypto === "undefined") {
    throw new Error(VERIFY_ERROR_MESSAGE);
  }

  let deviceId: string;
  if (typeof crypto.randomUUID === "function") {
    deviceId = crypto.randomUUID();
  } else if (crypto.getRandomValues) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    deviceId = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  } else {
    throw new Error(VERIFY_ERROR_MESSAGE);
  }

  localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
  return deviceId;
}

function isCode(error: any, expected: number): boolean {
  const candidates = [
    error?.status,
    error?.code,
    error?.response?.data?.code,
    error?.response?.code,
  ];
  return candidates.some((value) => value === expected || value === String(expected));
}

export function isTurnstileRelatedError(error: unknown): boolean {
  const err = error as any;
  return (
    isCode(err, TURNSTILE_VERIFY_FAILED_CODE) ||
    isCode(err, TURNSTILE_UNAVAILABLE_CODE) ||
    err?.message === VERIFY_ERROR_MESSAGE
  );
}

/** 将服务端 / 客户端人机验证错误转成用户可见文案。 */
export function resolveAuthErrorMessage(error: unknown, fallback: string): string {
  const err = error as any;
  if (
    isCode(err, TURNSTILE_VERIFY_FAILED_CODE) ||
    err?.message === VERIFY_ERROR_MESSAGE ||
    err?.msg === VERIFY_ERROR_MESSAGE
  ) {
    return VERIFY_ERROR_MESSAGE;
  }
  if (isCode(err, TURNSTILE_UNAVAILABLE_CODE)) {
    return (
      err?.msg ||
      err?.response?.msg ||
      err?.response?.data?.msg ||
      err?.message ||
      fallback
    );
  }
  return (
    err?.response?.msg ||
    err?.msg ||
    err?.error ||
    err?.detail ||
    err?.message ||
    fallback
  );
}

function isDisplayed(el: Element): boolean {
  let node: Element | null = el;
  while (node && node instanceof HTMLElement) {
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") return false;
    node = node.parentElement;
  }
  return true;
}

/** 当前可见的登录/注册卡片内验证槽；弹窗打开时优先用弹窗内的槽。 */
export function findTurnstileSlot(): HTMLElement | null {
  const slots = [...document.querySelectorAll<HTMLElement>(`[${TURNSTILE_SLOT_ATTR}]`)];
  const visible = slots.filter((el) => el.isConnected && isDisplayed(el));
  if (!visible.length) return null;
  const inDialog = [...visible].reverse().find(
    (el) => el.closest('[role="dialog"]') || el.closest(".auth-modal-overlay.show")
  );
  return inDialog ?? visible[visible.length - 1] ?? null;
}

function resolveTurnstileHost(): { host: HTMLElement; ephemeral: boolean } {
  const slot = findTurnstileSlot();
  if (slot) return { host: slot, ephemeral: false };

  const host = document.createElement("div");
  host.setAttribute("aria-label", "人机验证");
  Object.assign(host.style, {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 0 16px",
  });
  const card = document.querySelector<HTMLElement>(
    ".auth-modal-overlay.show .auth-modal, [role='dialog'], .auth-page__panel"
  );
  const submit = card?.querySelector<HTMLElement>(
    "button[type='submit'], .auth-page__submit, .forgot-password-modal__submit"
  );
  if (submit?.parentElement) {
    submit.parentElement.insertBefore(host, submit);
  } else if (card) {
    card.appendChild(host);
  } else {
    document.body.appendChild(host);
  }
  return { host, ephemeral: true };
}

export async function getTurnstileHeaders(
  action: TurnstileAction
): Promise<Record<string, string>> {
  const siteKey = TURNSTILE_SITE_KEY.trim();
  if (!siteKey) throw new Error(VERIFY_ERROR_MESSAGE);

  try {
    const deviceId = getDeviceId();
    const turnstile = await loadTurnstile();
    const { host, ephemeral } = resolveTurnstileHost();
    host.setAttribute("aria-label", "人机验证");
    host.replaceChildren();
    host.style.opacity = "0";

    const flickerStyle = document.createElement("style");
    flickerStyle.textContent =
      ".auth-page iframe,[role='dialog'] iframe{opacity:0!important}";
    document.head.appendChild(flickerStyle);
    const revealTimer = window.setTimeout(() => {
      host.style.opacity = "";
      flickerStyle.remove();
    }, 400);

    let widgetId: string | undefined;
    try {
      const token = await new Promise<string>((resolve, reject) => {
        let settled = false;
        let timeoutId: number | undefined;
        const finish = (value?: string) => {
          if (settled) return;
          settled = true;
          if (timeoutId !== undefined) window.clearTimeout(timeoutId);
          if (value) resolve(value);
          else reject(new Error(VERIFY_ERROR_MESSAGE));
        };
        timeoutId = window.setTimeout(() => finish(), VERIFY_TIMEOUT_MS);

        try {
          widgetId = turnstile.render(host, {
            sitekey: siteKey,
            action,
            cData: deviceId,
            execution: "execute",
            appearance: "always",
            callback: (value) => finish(value),
            "error-callback": () => finish(),
            "expired-callback": () => finish(),
            "timeout-callback": () => finish(),
          });
          turnstile.execute(widgetId);
        } catch {
          finish();
        }
      });

      const successMessage = document.createElement("div");
      successMessage.setAttribute("role", "status");
      successMessage.textContent = "验证成功";
      successMessage.style.color = "#15803d";
      successMessage.style.fontSize = "13px";
      successMessage.style.lineHeight = "20px";
      successMessage.style.marginTop = "8px";
      host.appendChild(successMessage);
      await new Promise<void>((resolve) => window.setTimeout(resolve, 1000));

      return {
        "X-Turnstile-Token": token,
        "X-Device-Id": deviceId,
      };
    } finally {
      window.clearTimeout(revealTimer);
      flickerStyle.remove();
      host.style.opacity = "";
      if (widgetId) turnstile.remove(widgetId);
      host.replaceChildren();
      if (ephemeral) host.remove();
    }
  } catch {
    throw new Error(VERIFY_ERROR_MESSAGE);
  }
}
